import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Copy, Check, Landmark, ShieldCheck, ArrowLeft, Loader2, Mail, AlertTriangle, Zap } from "lucide-react";
import { useCart } from "../context/CartContext";
import { createBankTransfer, money, getSpin, clearSpin } from "../lib/api";
import { track } from "../lib/pixel";
import { toast } from "sonner";

const CopyRow = ({ label, value }) => {
  const [done, setDone] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setDone(true);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#27272A]" data-testid={`bank-row-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="min-w-0">
        <p className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
        <p className="font-mono-tech text-sm text-white font-medium break-all">{value}</p>
      </div>
      <button
        onClick={copy}
        className={`shrink-0 flex items-center gap-1.5 border px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest transition-colors ${
          done ? "border-emerald-400 text-emerald-400" : "border-[#27272A] text-zinc-300 hover:border-accent hover:text-accent"
        }`}
      >
        {done ? <Check size={13} /> : <Copy size={13} />} {done ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

export default function BankTransfer() {
  const { items, subtotal, count, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", address: "", suburb: "", state: "", postcode: "" });
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!items.length && !order) navigate("/shop");
    else if (items.length) track("InitiateCheckout", { value: subtotal, currency: "AUD", num_items: items.length });
  }, []); // eslint-disable-line

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = Object.values(form).every((v) => v.trim()) && ack;

  const submit = async () => {
    if (!valid) {
      toast.error("Please complete all fields and confirm the declaration.");
      return;
    }
    setLoading(true);
    try {
      const res = await createBankTransfer(form, items, ack);
      setOrder(res);
      track("Purchase", { value: res.total, currency: "AUD", order_id: res.order_ref });
      clear();
      clearSpin();
      window.dispatchEvent(new Event("rsa-spin-cleared"));
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order. Please try again.");
      setLoading(false);
    }
  };

  // ---------- CONFIRMATION ----------
  if (order) {
    const b = order.bank;
    const steps = [
      "Open your banking app and choose International / Overseas (SWIFT) transfer.",
      `Enter IBAN ${b.iban} and BIC/SWIFT ${b.bic}.`,
      `Beneficiary name: ${b.account_holder}.`,
      `Send the exact amount ${money(order.total)}.`,
      `Add your order reference ${order.order_ref} to the payment description.`,
      "We dispatch once funds arrive (1–3 business days for international transfers).",
    ];
    return (
      <div className="pt-24 md:pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="bank-confirmation">
            <div className="flex items-center gap-2 text-emerald-400 mb-3">
              <Check size={18} /><span className="font-mono-tech text-[11px] uppercase tracking-widest">Order reserved</span>
            </div>
            <h1 className="font-display font-black tracking-tighter text-5xl leading-none">TRANSFER TO CONFIRM</h1>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              Order <strong className="text-white font-mono-tech">{order.order_ref}</strong> is reserved. Complete the international bank transfer below to confirm dispatch.
              {order.email_sent && (
                <span className="inline-flex items-center gap-1.5 ml-1 text-zinc-300"><Mail size={14} className="text-accent" /> A copy has been emailed to you.</span>
              )}
            </p>

            <div className="flex items-baseline justify-between mt-8 pb-4 border-b border-[#27272A]">
              <span className="font-mono-tech text-xs uppercase tracking-widest text-zinc-500">Amount to transfer</span>
              <span className="font-display font-black text-3xl">{money(order.total)}</span>
            </div>

            <h2 className="font-display font-extrabold text-xl tracking-tight mt-8 mb-3 flex items-center gap-2">
              <Landmark size={18} className="text-accent" /> Bank details
            </h2>
            <div className="border border-[#27272A] bg-ink-800 clip-corner">
              <CopyRow label="Account name" value={b.account_name} />
              <CopyRow label="Account holder" value={b.account_holder} />
              <CopyRow label="IBAN" value={b.iban} />
              <CopyRow label="BIC / SWIFT" value={b.bic} />
              <CopyRow label="Bank address" value={b.bank_address} />
              <CopyRow label="Amount" value={`${order.total.toFixed(2)} AUD`} />
              <CopyRow label="Reference" value={order.order_ref} />
            </div>
            <p className="font-mono-tech text-[11px] text-zinc-500 mt-2 leading-relaxed">
              Beneficiary address: {b.beneficiary_address}
            </p>

            <div className="border border-accent/40 bg-accent/5 p-4 clip-corner mt-4 flex gap-2">
              <AlertTriangle size={15} className="text-accent shrink-0 mt-0.5" />
              <p className="text-xs text-zinc-300 leading-relaxed">
                You <strong>must</strong> include the reference <strong className="text-accent font-mono-tech">{order.order_ref}</strong> in your transfer so we can match your payment.
              </p>
            </div>

            <h2 className="font-display font-extrabold text-xl tracking-tight mt-8 mb-4">How to send the transfer</h2>
            <ol className="space-y-3">
              {steps.map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 h-6 w-6 flex items-center justify-center bg-accent text-white font-mono-tech text-xs font-bold rounded-full">{i + 1}</span>
                  <span className="text-zinc-300 text-sm leading-relaxed pt-0.5">{s}</span>
                </li>
              ))}
            </ol>

            <Link to="/shop" className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-display font-bold uppercase tracking-wide px-7 py-4 mt-10 transition-colors clip-corner">
              Continue Shopping
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  // ---------- FORM ----------
  const fields = [
    { label: "Full name", k: "name", type: "text", w: "sm:col-span-2" },
    { label: "Email", k: "email", type: "email", w: "sm:col-span-2" },
    { label: "Shipping address", k: "address", type: "text", w: "sm:col-span-2" },
    { label: "Suburb", k: "suburb", type: "text", w: "" },
    { label: "State", k: "state", type: "text", w: "" },
    { label: "Postcode", k: "postcode", type: "text", w: "" },
  ];

  return (
    <div className="pt-24 md:pt-28 pb-24">
      <div className="max-w-2xl mx-auto px-5 md:px-10">
        <Link to="/shop" className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-zinc-400 hover:text-accent transition-colors">
          <ArrowLeft size={14} /> Back to shop
        </Link>
        <span className="block font-mono-tech text-xs tracking-[0.3em] uppercase text-accent mt-8">/ Direct deposit</span>
        <h1 className="font-display font-black tracking-tighter text-5xl leading-none mt-3">BANK TRANSFER CHECKOUT</h1>
        <p className="text-zinc-400 mt-4 leading-relaxed">
          Pay by international bank transfer. Enter your shipping details and we'll email you the bank details and a step-by-step transfer guide with your order reference.
        </p>

        <div className="flex items-center justify-between mt-8 pb-4 border-b border-[#27272A]">
          <span className="font-mono-tech text-xs uppercase tracking-widest text-zinc-500">{count} item{count === 1 ? "" : "s"} · merchandise subtotal</span>
          <span className="font-display font-black text-2xl">{money(subtotal)}</span>
        </div>
        <p className="font-mono-tech text-[10px] text-zinc-600 mt-2">Free tracked shipping on orders over $250 (otherwise $15 flat) — 7–14 business days. Bundle, member & spin savings are applied to the final amount on the next step.</p>

        {getSpin() && (
          <div className="mt-3 border border-accent/40 bg-accent/5 px-4 py-3 flex items-center gap-2 clip-corner" data-testid="bt-spin-applied">
            <Zap size={14} className="text-accent shrink-0" />
            <p className="font-mono-tech text-[11px] uppercase tracking-widest text-zinc-300">
              Spin code <span className="text-accent font-bold">{getSpin().code}</span> —{" "}
              {getSpin().prize_type === "shipping"
                ? "free shipping applied to your order"
                : getSpin().prize_type === "free_tadalafil"
                ? "free Tadalafil x100 will be added to your order"
                : getSpin().prize_type === "free_bacwater"
                ? "free BAC Water 5-pack will be added to your order"
                : `extra ${getSpin().percent}% off applied to your order`}
            </p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          {fields.map((f) => (
            <label key={f.k} className={`block ${f.w}`}>
              <span className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500">{f.label}</span>
              <input
                type={f.type}
                value={form[f.k]}
                onChange={set(f.k)}
                autoComplete={f.k === "postcode" ? "postal-code" : f.k === "address" ? "street-address" : f.k}
                className="w-full bg-ink-900 border border-[#27272A] focus:border-accent outline-none px-3 py-2.5 mt-1.5 text-white transition-colors"
                data-testid={`bt-${f.k}`}
              />
            </label>
          ))}
        </div>

        <label className="flex gap-2.5 items-start cursor-pointer mt-6" data-testid="bt-ack">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#FF331F] shrink-0" />
          <span className="text-[11px] text-zinc-400 leading-relaxed">
            I am <strong className="text-zinc-200">18+</strong> and confirm these products are for <strong className="text-zinc-200">laboratory research use only — not for human consumption</strong>.
          </span>
        </label>

        <button
          onClick={submit}
          disabled={loading || !valid}
          className="w-full mt-6 bg-accent hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed text-white font-display font-bold uppercase tracking-wide text-lg py-4 flex items-center justify-center gap-2 transition-colors clip-corner"
          data-testid="bt-submit"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Landmark size={18} />}
          {loading ? "Placing order…" : "Get Bank Transfer Details"}
        </button>
        <p className="flex items-center justify-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 mt-3">
          <ShieldCheck size={13} className="text-accent" /> No card needed · pay from your banking app
        </p>
      </div>
    </div>
  );
}
